// Auth.js — single source of truth for client teardown
import { AUTH_BASE } from "./data";

// Shared key so SSE doesn't hardcode it
export const SSE_LAST_ID_KEY = "sse:lastEventId";

/** Small helper to tear down any client-side state on logout/401s */
export function clearClientState() {
  // Tell SSE to shut down (SSE.js listens for this, but a direct event also helps)
  try {
    window.dispatchEvent(new CustomEvent("sse:disconnect", { detail: { at: Date.now() } }));
  } catch {}

  // Clear the SSE last-id hint (so a fresh head() is used next time)
  try { localStorage.removeItem(SSE_LAST_ID_KEY); } catch {}

  // Clear local/session storage
  try { localStorage.clear(); } catch {}
  try { sessionStorage.clear(); } catch {}

  // Best-effort: clear Cache API
  try {
    if (typeof caches !== "undefined" && caches?.keys) {
      caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
    }
  } catch {}
}

/** Verify session and return current user { id, tenant_id, email, is_admin } */
export async function getSessionUser() {
  const res = await fetch(`${AUTH_BASE}/auth/me`, {
    method: "GET",
    credentials: "include",
    mode: "cors",
    headers: { Accept: "application/json" },
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success !== true) {
    if (res.status === 401) {
      try {
        window.dispatchEvent(new CustomEvent("unauthenticated", { detail: { at: Date.now() } }));
      } catch {}
      clearClientState();
    }
    const err = new Error(json?.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return json.data;
}

/** Cheap auth check (use getSessionUser so we also learn admin status) */
export async function checkAuth() {
  await getSessionUser();
  return true;
}

export async function login({ email, password }) {
  const res = await fetch(`${AUTH_BASE}/auth/login`, {
    method: "POST",
    credentials: "include",
    mode: "cors",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success !== true) {
    const err = new Error(json?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = json;
    throw err;
  }
  return true;
}

/** Log out on the server, then clear all client-side state. */
export async function logout() {
  try {
    const res = await fetch(`${AUTH_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      headers: { Accept: "application/json" },
    });
    await res.json().catch(() => ({}));
  } catch {
    // ignore network errors — still clear local state
  } finally {
    clearClientState();
  }
  return true;
}
